import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import homeMetaDescriptions from "../homeMetaDescriptions.json";
import tutorialVideoMetadata from "../tutorialVideoMetadata.json";
import faqContent from "../faqContent.json";
import globalFeatureContent from "../globalFeatureContent.json";
import sampleVideoSnapshot from "../sampleVideos.json";
import {
  isSupportedLanguage,
  normalizeLanguageCode,
  SUPPORTED_LANGUAGE_CODES,
} from "../supportedLanguages";
import { getYouTubeRankingContent } from "../youtubeRankingData";
import {
  BLOG_CONTENT_LANGUAGES,
  BlogPost,
  blogPosts,
  getBlogContentLanguage,
  getBlogLabels,
  isPrimaryBlogLanguage,
} from "../blogContent";
import { usePublicBlogPosts } from "../usePublicBlogPosts";
import useCaseContent from "../useCaseContent.json";
import productCatalog from "../productCatalog.json";
import {
  UPDATE_CONTENT_LANGUAGES,
  UpdateNote,
  getUpdateContentLanguage,
  getUpdateLabels,
  updateNotes,
} from "../updateContent";

type SeoContent = {
  title: string;
  description: string;
};

type SeoMap = Record<string, SeoContent>;

type LocalizedSeoKeyConfig = {
  title: string;
  description: string[];
};

type TranslateSeoValue = (key: string, fallback: string) => string;

type FaqLabels = (typeof faqContent)["en"];
type GlobalFeatureContent = (typeof globalFeatureContent)["en"];
type StructuredFaqItem = {
  question: string;
  answer: string;
};

type TutorialVideo = {
  videoId: string;
  name: string;
  uploadDate: string;
};

type TutorialVideoSet = {
  language: string;
  shorts: TutorialVideo;
  longform: TutorialVideo;
};

const HOME_META_DESCRIPTIONS: Record<string, string> = homeMetaDescriptions;
const TOP_TUTORIAL_VIDEOS: Record<string, TutorialVideoSet> = tutorialVideoMetadata;
const FAQ_CONTENT_BY_LANGUAGE = faqContent as Record<string, FaqLabels>;
const GLOBAL_FEATURE_CONTENT_BY_LANGUAGE = globalFeatureContent as Record<
  string,
  GlobalFeatureContent
>;

const TUTORIAL_LOCALE_BY_LANGUAGE: Record<string, string> = {
  en: "en-us",
  de: "de-de",
  "es-es": "es-es",
  "es-mx": "es-es",
  fr: "fr-fr",
  hi: "hi-in",
  ja: "ja-jp",
  ko: "ko-kr",
  "pt-br": "pt-br",
  "pt-pt": "pt-br",
  "zh-cn": "zh-cn",
  "zh-tw": "zh-cn",
};

const getTopTutorialVideos = (languageCode: string): TutorialVideoSet => {
  const normalizedLanguage = languageCode.trim().replace(/_/g, "-").toLowerCase();
  const tutorialLocale =
    TUTORIAL_LOCALE_BY_LANGUAGE[normalizedLanguage] ?? normalizedLanguage;
  return TOP_TUTORIAL_VIDEOS[tutorialLocale] ?? TOP_TUTORIAL_VIDEOS["en-us"];
};

const SITE_URL = (process.env.REACT_APP_SITE_URL ?? "https://autovid.net").replace(
  /\/+$/,
  ""
);

const INDEXABLE_ROUTE_PATHS = new Set<string>([
  "/",
  "/privacy-policy",
  "/terms-of-service",
  "/refund-policy",
  "/ai-content-policy",
  "/sample-videos",
  "/youtube-rankings",
  "/how-to-use",
  "/download",
  "/features",
  "/pricing",
  "/faq",
  "/support",
  "/updates",
  "/ai-shorts-maker",
  "/marketing-video-maker",
]);
const USE_CASE_ROUTE_PATHS = new Set(["/ai-shorts-maker", "/marketing-video-maker"]);
const USE_CASE_LANGUAGES = ["en", "ko", "ja"];

const NOINDEX_ROUTE_PREFIXES: string[] = [
  "/login",
  "/login-email",
  "/mypage",
  "/inquiries",
  "/task-management",
];

const LOCALIZED_SEO_KEYS: Record<string, LocalizedSeoKeyConfig> = {
  "/": {
    title: "home_page.hero.messages.0.title",
    description: [
      "home_page.hero.messages.0.subtitle1",
      "home_page.hero.messages.0.subtitle2",
    ],
  },
  "/joinIn": {
    title: "join_in.title",
    description: ["terms_of_service.introduction"],
  },
  "/features": {
    title: "features_page.hero.title",
    description: ["features_page.hero.subtitle"],
  },
  "/sample-videos": {
    title: "sample_videos_page.hero.title",
    description: ["sample_videos_page.hero.intro"],
  },
  "/youtube-rankings": {
    title: "youtube_rankings.page.title",
    description: ["youtube_rankings.page.description"],
  },
  "/pricing": {
    title: "pricing_page.hero.title",
    description: ["pricing_page.hero.subtitle"],
  },
  "/how-to-use": {
    title: "how_to_use.hero.title",
    description: ["how_to_use.hero.subtitle"],
  },
  "/download": {
    title: "navigation.download",
    description: ["home_page.final.lead1", "home_page.final.lead2"],
  },
  "/support": {
    title: "support_page.title",
    description: ["support_page.subtitle"],
  },
  "/support/chatbot": {
    title: "floating_support_chat.label",
    description: ["floating_support_chat.confirm_description"],
  },
  "/privacy-policy": {
    title: "privacy_policy.title",
    description: ["privacy_policy.introduction.text1"],
  },
  "/terms-of-service": {
    title: "terms_of_service.title",
    description: ["terms_of_service.introduction"],
  },
  "/refund-policy": {
    title: "refund_policy_page.title",
    description: ["refund_policy_page.intro"],
  },
  "/ai-content-policy": {
    title: "ai_content_policy_page.title",
    description: ["ai_content_policy_page.intro"],
  },
};

const EN_SEO_BY_PATH: SeoMap = {
  "/": {
    title: "AI Video Creation for YouTube | AUTOVID",
    description:
      "Create and publish YouTube-ready videos faster with AI templates, subtitles, thumbnails, and workflow automation.",
  },
  "/joinIn": {
    title: "Create Your AUTOVID Account",
    description:
      "Sign up for AUTOVID and start producing AI-assisted YouTube videos with automated content workflows.",
  },
  "/features": {
    title: "AUTOVID Features",
    description:
      "Explore AUTOVID features including templates, thumbnail generation, subtitle sync, and one-click publishing support.",
  },
  "/sample-videos": {
    title: "AUTOVID Sample Result Videos",
    description:
      "Watch Shorts, long-form, and marketing video examples created with AUTOVID, with direct links to each original YouTube video.",
  },
  "/youtube-rankings": {
    title: "YouTube Rankings and Rising Videos | AUTOVID",
    description:
      "Explore YouTube mostPopular rankings and rising videos by country and category.",
  },
  "/pricing": {
    title: "AUTOVID Pricing",
    description:
      "Compare AUTOVID subscription plans and credit packs for creators and teams.",
  },
  "/how-to-use": {
    title: "How To Use AUTOVID",
    description:
      "Learn how to create, edit, and publish AI-generated videos in AUTOVID step by step.",
  },
  "/download": {
    title: "Download AUTOVID",
    description:
      "Download AUTOVID and start building AI-powered video workflows for your content.",
  },
  "/privacy-policy": {
    title: "AUTOVID Privacy Policy",
    description:
      "Read how AUTOVID handles personal information, data processing, and user privacy.",
  },
  "/terms-of-service": {
    title: "AUTOVID Terms of Service",
    description:
      "Review the terms and conditions for using AUTOVID services.",
  },
  "/refund-policy": {
    title: "AUTOVID Refund Policy",
    description:
      "Check AUTOVID refund conditions and payment policy details before purchase.",
  },
  "/ai-content-policy": {
    title: "AUTOVID AI Content Policy",
    description:
      "Review AUTOVID rules for responsible AI-assisted visual and video content creation.",
  },
  "/faq": {
    title: "AUTOVID FAQ",
    description:
      "Get answers about AI Shorts and long-form creation, timeline editing, subtitles, image motion, 30-language workflows, YouTube upload, pricing, and commercial use.",
  },
  "/support": {
    title: "AUTOVID Customer Support",
    description:
      "Contact AUTOVID customer support and submit your inquiry with your account information.",
  },
  "/support/chatbot": {
    title: "AUTOVID AI Support Chatbot",
    description:
      "Ask AUTOVID's AI support chatbot simple questions about usage, billing, refunds, credits, and account support.",
  },
};

const KO_SEO_BY_PATH: SeoMap = {
  "/": {
    title: "유튜브 AI 영상 제작 | AUTOVID",
    description:
      "템플릿, 자막, 썸네일, 자동화 기능으로 유튜브 영상을 더 빠르게 제작하세요.",
  },
  "/joinIn": {
    title: "AUTOVID 회원가입",
    description: "AUTOVID 계정을 만들고 AI 기반 영상 제작을 시작하세요.",
  },
  "/features": {
    title: "AUTOVID 기능 소개",
    description:
      "템플릿, 썸네일 생성, 자막 동기화, 업로드 지원 등 AUTOVID 핵심 기능을 확인하세요.",
  },
  "/sample-videos": {
    title: "AUTOVID 예시 결과 영상",
    description:
      "AUTOVID로 제작한 쇼츠, 롱폼, 마케팅 영상 등 실제 예시 결과와 원본 YouTube 영상을 확인하세요.",
  },
  "/pricing": {
    title: "AUTOVID 요금제",
    description: "AUTOVID 구독 플랜과 크레딧 팩을 비교해 보세요.",
  },
  "/how-to-use": {
    title: "AUTOVID 사용 방법",
    description: "AUTOVID에서 영상 생성부터 편집까지 단계별 사용법을 확인하세요.",
  },
  "/download": {
    title: "AUTOVID 다운로드",
    description: "AUTOVID를 다운로드하고 AI 영상 제작 워크플로우를 시작하세요.",
  },
  "/privacy-policy": {
    title: "AUTOVID 개인정보처리방침",
    description: "AUTOVID의 개인정보 처리 및 보호 정책을 확인하세요.",
  },
  "/terms-of-service": {
    title: "AUTOVID 이용약관",
    description: "AUTOVID 서비스 이용 조건과 약관을 확인하세요.",
  },
  "/refund-policy": {
    title: "AUTOVID 환불정책",
    description: "결제 전 AUTOVID 환불 조건과 정책을 확인하세요.",
  },
  "/ai-content-policy": {
    title: "AUTOVID AI 콘텐츠 정책",
    description:
      "AUTOVID의 AI 보조 영상 및 시각 자료 생성 이용 기준과 금지 용도를 확인하세요.",
  },
  "/faq": {
    title: "AUTOVID FAQ",
    description:
      "AI 쇼츠·롱폼 생성, 타임라인 편집, 자막·이미지 모션, 30개 언어, 유튜브 업로드, 요금과 상업적 이용에 관한 답변을 확인하세요.",
  },
  "/support": {
    title: "AUTOVID 고객지원",
    description: "고객지원 페이지에서 문의를 접수하고 답변을 받을 수 있습니다.",
  },
  "/support/chatbot": {
    title: "AUTOVID AI 상담 챗봇",
    description:
      "AUTOVID AI 상담 챗봇에서 사용법, 결제, 환불, 크레딧, 계정 관련 간단한 문의를 확인하세요.",
  },
};

const DEFAULT_EN_SEO: SeoContent = {
  title: "AUTOVID",
  description:
    "AUTOVID helps creators produce AI-assisted videos quickly with templates and automated workflows.",
};

const DEFAULT_KO_SEO: SeoContent = {
  title: "AUTOVID",
  description:
    "AUTOVID는 템플릿과 자동화 워크플로우로 AI 영상 제작 속도를 높여줍니다.",
};

const getLocalizedFaqItems = (
  lang: string,
  translateSeoValue: TranslateSeoValue,
): StructuredFaqItem[] => {
  const normalizedLanguage = normalizeLanguageCode(lang);
  const labels =
    FAQ_CONTENT_BY_LANGUAGE[normalizedLanguage] || FAQ_CONTENT_BY_LANGUAGE.en;
  const globalFeatures =
    GLOBAL_FEATURE_CONTENT_BY_LANGUAGE[normalizedLanguage] ||
    GLOBAL_FEATURE_CONTENT_BY_LANGUAGE.en;
  const translate = (key: string) => translateSeoValue(key, "");
  const joinTranslations = (keys: string[]) =>
    keys.map(translate).filter(Boolean).join(" ");

  return [
    {
      question: labels.productQuestion,
      answer: [
        translate("home_page.hero.messages.0.subtitle2"),
        translate("features_page.hero.subtitle"),
      ]
        .filter(Boolean)
        .join(" "),
    },
    {
      question: labels.shortsLongformQuestion,
      answer: joinTranslations([
        "features_page.templates.subtitle",
        "features_page.templates.bullet1",
      ]),
    },
    {
      question: labels.beginnerQuestion,
      answer: globalFeatures.guidedWorkflowText,
    },
    {
      question: labels.effectsQuestion,
      answer: [
        globalFeatures.subtitleEffectsText,
        translate("how_to_use.steps.image_motion.body"),
        translate("features_page.templates.subtitle"),
      ]
        .filter(Boolean)
        .join(" "),
    },
    {
      question: labels.editorQuestion,
      answer: labels.editorAnswer,
    },
    {
      question: labels.marketingQuestion,
      answer: `${globalFeatures.marketingText} ${globalFeatures.globalText}`,
    },
    {
      question: labels.uploadQuestion,
      answer: joinTranslations([
        "features_page.upload.subtitle",
        "features_page.upload.bullet1",
      ]),
    },
    {
      question: labels.creditsQuestion,
      answer: labels.creditsAnswer,
    },
    {
      question: labels.commercialQuestion,
      answer: labels.commercialAnswer,
    },
    {
      question: labels.supportQuestion,
      answer: [
        translate("how_to_use.hero.subtitle"),
        translate("support_page.subtitle"),
      ]
        .filter(Boolean)
        .join(" "),
    },
  ];
};

const normalizePath = (path: string): string => {
  if (!path) {
    return "/";
  }

  let normalizedPath = path.split("?")[0].split("#")[0];
  if (!normalizedPath.startsWith("/")) {
    normalizedPath = `/${normalizedPath}`;
  }

  if (normalizedPath.length > 1) {
    normalizedPath = normalizedPath.replace(/\/+$/, "");
  }

  return normalizedPath || "/";
};

const stripLanguagePrefix = (pathname: string, lang: string): string => {
  const prefix = `/${lang}`;
  if (pathname === prefix || pathname === `${prefix}/`) {
    return "/";
  }

  if (pathname.startsWith(`${prefix}/`)) {
    return pathname.slice(prefix.length);
  }

  return pathname;
};

const isPathUnderPrefix = (path: string, prefix: string): boolean =>
  path === prefix || path.startsWith(`${prefix}/`);

const isIndexablePath = (path: string): boolean => {
  if (NOINDEX_ROUTE_PREFIXES.some((prefix) => isPathUnderPrefix(path, prefix))) {
    return false;
  }

  return INDEXABLE_ROUTE_PATHS.has(path);
};

const toLocalizedPath = (lang: string, routePath: string): string =>
  `/${lang}${routePath === "/" ? "" : routePath}`;

const toAbsoluteUrl = (path: string): string => `${SITE_URL}${path}`;

const upsertMetaTag = (
  attributeName: "name" | "property",
  attributeValue: string,
  content: string
): void => {
  let tag = document.head.querySelector(
    `meta[${attributeName}="${attributeValue}"]`
  ) as HTMLMetaElement | null;

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attributeName, attributeValue);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
};

const removeSeoLinks = (): void => {
  document.head
    .querySelectorAll(
      'link[rel="canonical"], link[rel="alternate"][hreflang], link[rel="alternate"][type="text/markdown"]'
    )
    .forEach((node) => node.parentNode?.removeChild(node));
};

const addManagedLink = (
  rel: string,
  href: string,
  extraAttributes?: Record<string, string>
): void => {
  const link = document.createElement("link");
  link.setAttribute("rel", rel);
  link.setAttribute("href", href);
  link.setAttribute("data-seo-managed", "true");

  if (extraAttributes) {
    Object.entries(extraAttributes).forEach(([key, value]) => {
      link.setAttribute(key, value);
    });
  }

  document.head.appendChild(link);
};

const upsertStructuredData = (
  routePath: string,
  lang: string,
  canonicalUrl: string,
  title: string,
  description: string,
  translateSeoValue: TranslateSeoValue,
): void => {
  const imageUrl = `${SITE_URL}/assets/images/BANNER_LONG_1280.png`;
  const schema: Array<Record<string, unknown>> = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "DreamCraft",
      legalName: "DreamCraft",
      url: SITE_URL,
      logo: `${SITE_URL}/logo512.png`,
      email: "support@dreamcraft.co.kr",
      brand: {
        "@type": "Brand",
        name: "AUTOVID",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      name: "AUTOVID",
      url: SITE_URL,
      inLanguage: lang,
      publisher: {
        "@id": `${SITE_URL}/#organization`,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "@id": `${SITE_URL}/#software`,
      name: "AUTOVID",
      applicationCategory: "MultimediaApplication",
      operatingSystem: "Windows",
      url: SITE_URL,
      description,
      image: imageUrl,
      offers: productCatalog.subscriptions.map((plan) => ({
        "@type": "Offer",
        name: plan.name,
        price: plan.price.replace("$", ""),
        priceCurrency: productCatalog.currency,
        url: `${SITE_URL}/en/pricing`,
      })),
      publisher: {
        "@id": `${SITE_URL}/#organization`,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${canonicalUrl}#webpage`,
      url: canonicalUrl,
      name: title,
      description,
      inLanguage: lang,
      isPartOf: {
        "@id": `${SITE_URL}/#website`,
      },
      about: {
        "@id": `${SITE_URL}/#software`,
      },
    },
  ];

  if (routePath !== "/") {
    schema.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${canonicalUrl}#breadcrumb`,
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "AUTOVID",
          item: toAbsoluteUrl(toLocalizedPath(lang, "/")),
        },
        {
          "@type": "ListItem",
          position: 2,
          name: title,
          item: canonicalUrl,
        },
      ],
    });
  }

  if (routePath === "/faq") {
    schema.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: getLocalizedFaqItems(lang, translateSeoValue).map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    });
  }

  if (routePath === "/how-to-use") {
    const tutorialVideos = getTopTutorialVideos(lang);
    const videos = [tutorialVideos.shorts, tutorialVideos.longform];

    for (const video of videos) {
      schema.push({
        "@context": "https://schema.org",
        "@type": "VideoObject",
        "@id": `${canonicalUrl}#video-${video.videoId}`,
        name: video.name,
        description: `${video.name}. ${description}`,
        thumbnailUrl: `https://i.ytimg.com/vi/${video.videoId}/hqdefault.jpg`,
        uploadDate: video.uploadDate,
        embedUrl: `https://www.youtube.com/embed/${video.videoId}`,
        inLanguage: tutorialVideos.language,
        isPartOf: {
          "@id": `${canonicalUrl}#webpage`,
        },
        publisher: {
          "@id": `${SITE_URL}/#organization`,
        },
      });
    }
  }

  if (USE_CASE_ROUTE_PATHS.has(routePath)) {
    const isShorts = routePath === "/ai-shorts-maker";
    schema.push({
      "@context": "https://schema.org",
      "@type": "VideoObject",
      "@id": `${canonicalUrl}#example-video`,
      name: title,
      description,
      thumbnailUrl: isShorts
        ? "https://i.ytimg.com/vi/LKPPmixLoL8/hqdefault.jpg"
        : `${SITE_URL}/assets/images/BANNER_LONG_1280.png`,
      embedUrl: isShorts
        ? "https://www.youtube.com/embed/LKPPmixLoL8"
        : "https://customer-unwg1qdz6bidl9iw.cloudflarestream.com/10726afee5f3590afc032d7419115a4e/iframe?controls=true",
      isPartOf: { "@id": `${canonicalUrl}#webpage` },
      publisher: { "@id": `${SITE_URL}/#organization` },
    });
  }

  if (routePath === "/sample-videos") {
    schema.push({
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": `${canonicalUrl}#sample-videos`,
      name: title,
      numberOfItems: sampleVideoSnapshot.videos.length,
      itemListElement: sampleVideoSnapshot.videos.map((video, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@id": `${canonicalUrl}#video-${video.youtubeId}`,
        },
      })),
    });

    for (const video of sampleVideoSnapshot.videos) {
      schema.push({
        "@context": "https://schema.org",
        "@type": "VideoObject",
        "@id": `${canonicalUrl}#video-${video.youtubeId}`,
        name: video.title,
        description: `${video.title}. ${description}`,
        thumbnailUrl:
          video.thumbnailUrl ||
          `https://i.ytimg.com/vi/${video.youtubeId}/hqdefault.jpg`,
        uploadDate: video.publishedAt,
        embedUrl: `https://www.youtube.com/embed/${video.youtubeId}`,
        url: `https://www.youtube.com/watch?v=${video.youtubeId}`,
        isPartOf: {
          "@id": `${canonicalUrl}#webpage`,
        },
        publisher: {
          "@id": `${SITE_URL}/#organization`,
        },
      });
    }
  }

  const scriptId = "seo-json-ld";
  let script = document.getElementById(scriptId) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement("script");
    script.id = scriptId;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(schema);
};

const removeStructuredData = (): void => {
  document.getElementById("seo-json-ld")?.remove();
};

const getSeoContent = (
  routePath: string,
  lang: string,
  translateSeoValue?: TranslateSeoValue
): SeoContent => {
  if (USE_CASE_ROUTE_PATHS.has(routePath)) {
    const slug = routePath.slice(1) as keyof typeof useCaseContent;
    const contentLanguage = lang === "ko" || lang === "ja" ? lang : "en";
    const content = useCaseContent[slug][contentLanguage];
    return { title: `${content.title} | AUTOVID`, description: content.answer };
  }
  if (routePath === "/youtube-rankings") {
    const rankingContent = getYouTubeRankingContent(lang);
    return {
      title: `${rankingContent.heading} | AUTOVID`,
      description: rankingContent.description,
    };
  }

  const isKorean = lang.toLowerCase().startsWith("ko");
  const byPath = isKorean ? KO_SEO_BY_PATH : EN_SEO_BY_PATH;
  const fallback = isKorean ? DEFAULT_KO_SEO : DEFAULT_EN_SEO;
  const baseSeo = byPath[routePath] ?? fallback;
  const localizedKeys = LOCALIZED_SEO_KEYS[routePath];
  let seo = baseSeo;

  if (
    !isKorean &&
    lang !== "en" &&
    isSupportedLanguage(lang) &&
    localizedKeys &&
    translateSeoValue
  ) {
    const localizedTitle = translateSeoValue(localizedKeys.title, baseSeo.title);
    const localizedDescription = localizedKeys.description
      .map((key) => translateSeoValue(key, ""))
      .filter(Boolean)
      .join(" ");
    const title = localizedTitle.toUpperCase().includes("AUTOVID")
      ? localizedTitle
      : `${localizedTitle} | AUTOVID`;

    seo = {
      title,
      description: localizedDescription || baseSeo.description,
    };
  }

  if (routePath === "/") {
    const homeDescription = HOME_META_DESCRIPTIONS[lang];
    if (homeDescription) {
      return {
        ...seo,
        description: homeDescription,
      };
    }
  }

  return seo;
};

const upsertBlogStructuredData = (
  canonicalUrl: string,
  contentLanguage: "en" | "ko" | "ja",
  routePath: string,
  title: string,
  description: string,
  posts: BlogPost[],
  post: BlogPost | undefined
): void => {
  const schemas: Array<Record<string, unknown>> = [
    {
      "@context": "https://schema.org",
      "@type": routePath === "/blog" ? "CollectionPage" : "WebPage",
      name: title,
      description,
      url: canonicalUrl,
      inLanguage: contentLanguage,
      isPartOf: { "@id": `${SITE_URL}/#website` },
    },
  ];

  if (routePath === "/blog") {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "Blog",
      name: title,
      url: canonicalUrl,
      inLanguage: contentLanguage,
      blogPost: posts.map((item) => ({
        "@type": "BlogPosting",
        headline: item.translations[contentLanguage].title,
        url: `${canonicalUrl}/${item.slug}`,
        datePublished: item.date,
        dateModified: item.updated,
      })),
    });
  }

  if (post) {
    const translation = post.translations[contentLanguage];
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: translation.title,
      description: translation.description,
      datePublished: post.date,
      dateModified: post.updated,
      author: { "@type": "Organization", name: post.author },
      publisher: { "@id": `${SITE_URL}/#organization` },
      mainEntityOfPage: canonicalUrl,
      inLanguage: contentLanguage,
    });
    if (translation.faq.length > 0) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: translation.faq.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      });
    }
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "AUTOVID",
          item: `${SITE_URL}/${contentLanguage}`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: getBlogLabels(contentLanguage).title,
          item: `${SITE_URL}/${contentLanguage}/blog`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: translation.title,
          item: canonicalUrl,
        },
      ],
    });
  }

  let script = document.getElementById("seo-json-ld") as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement("script");
    script.id = "seo-json-ld";
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(schemas);
};

const upsertUpdateStructuredData = (
  canonicalUrl: string,
  contentLanguage: "en" | "ko" | "ja",
  routePath: string,
  title: string,
  description: string,
  update: UpdateNote | undefined,
): void => {
  const schemas: Array<Record<string, unknown>> = [
    {
      "@context": "https://schema.org",
      "@type": update ? "Article" : "CollectionPage",
      name: title,
      headline: update?.translations[contentLanguage].title,
      description,
      url: canonicalUrl,
      inLanguage: contentLanguage,
      datePublished: update?.date,
      dateModified: update?.updated,
      about: {
        "@type": "SoftwareApplication",
        name: "AUTOVID",
        applicationCategory: "MultimediaApplication",
        operatingSystem: "Windows",
      },
      isPartOf: { "@id": `${SITE_URL}/#website` },
    },
  ];

  if (!update) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: updateNotes.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.translations[contentLanguage].title,
        url: `${canonicalUrl}/${item.slug}`,
      })),
    });
  }

  let script = document.getElementById("seo-json-ld") as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement("script");
    script.id = "seo-json-ld";
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(schemas);
};

type SeoManagerProps = {
  lang: string;
};

const SeoManager: FC<SeoManagerProps> = ({ lang }) => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { posts } = usePublicBlogPosts();

  useEffect(() => {
    const routePath = normalizePath(stripLanguagePrefix(location.pathname, lang));
    const isBlogIndex = routePath === "/blog";
    const blogSlug = routePath.startsWith("/blog/")
      ? routePath.slice("/blog/".length)
      : "";
    const blogPost = posts.find((post) => post.slug === blogSlug);
    const isStaticBlogPost = blogPosts.some((post) => post.slug === blogSlug);
    const isBlogRoute = isBlogIndex || Boolean(blogPost);
    const isUpdateIndex = routePath === "/updates";
    const updateSlug = routePath.startsWith("/updates/")
      ? routePath.slice("/updates/".length)
      : "";
    const updateNote = updateNotes.find((update) => update.slug === updateSlug);
    const isUpdateRoute = isUpdateIndex || Boolean(updateNote);
    const primaryBlogLanguage = isPrimaryBlogLanguage(lang);
    const primaryUpdateLanguage = UPDATE_CONTENT_LANGUAGES.includes(
      getUpdateContentLanguage(lang),
    ) && ["en", "ko", "ja"].includes(lang.toLowerCase().split("-")[0]);
    const indexable = isSupportedLanguage(lang) && (
      isBlogRoute
        ? primaryBlogLanguage
        : isUpdateRoute
        ? primaryUpdateLanguage
        : isIndexablePath(routePath)
    );
    const translateSeoValue: TranslateSeoValue = (key, fallback) =>
      t(key, { lng: lang, defaultValue: fallback });
    const contentLanguage = getBlogContentLanguage(lang);
    const labels = getBlogLabels(lang);
    const blogTranslation = blogPost?.translations[contentLanguage];
    const updateContentLanguage = getUpdateContentLanguage(lang);
    const updateLabels = getUpdateLabels(lang);
    const updateTranslation = updateNote?.translations[updateContentLanguage];
    const seo = isBlogRoute
      ? {
          title: blogTranslation
            ? `${blogTranslation.title} | AUTOVID`
            : `${labels.title} | AUTOVID`,
          description: blogTranslation?.description ?? labels.lead,
        }
      : isUpdateRoute
      ? {
          title: updateTranslation
            ? `${updateTranslation.title} | AUTOVID`
            : `${updateLabels.title} | AUTOVID`,
          description: updateTranslation?.summary ?? updateLabels.lead,
        }
      : getSeoContent(routePath, lang, translateSeoValue);
    const canonicalLanguage =
      (isBlogRoute && !primaryBlogLanguage) ||
      (isUpdateRoute && !primaryUpdateLanguage) ||
      (USE_CASE_ROUTE_PATHS.has(routePath) && !USE_CASE_LANGUAGES.includes(lang))
        ? "en"
        : lang;
    const canonicalPath = toLocalizedPath(canonicalLanguage, routePath);
    const canonicalUrl = toAbsoluteUrl(canonicalPath);

    document.title = seo.title;

    upsertMetaTag("name", "description", seo.description);
    upsertMetaTag(
      "name",
      "robots",
      indexable ? "index, follow" : "noindex, follow"
    );
    upsertMetaTag(
      "name",
      "googlebot",
      indexable ? "index, follow" : "noindex, follow"
    );
    upsertMetaTag(
      "property",
      "og:type",
      blogPost || updateNote ? "article" : "website",
    );
    upsertMetaTag("property", "og:site_name", "AUTOVID");
    upsertMetaTag("property", "og:title", seo.title);
    upsertMetaTag("property", "og:description", seo.description);
    upsertMetaTag("property", "og:url", canonicalUrl);
    upsertMetaTag(
      "property",
      "og:image",
      blogPost?.image?.path
        ? toAbsoluteUrl(blogPost.image.path)
        : `${SITE_URL}/assets/images/BANNER_LONG_1280.png`
    );
    upsertMetaTag("name", "twitter:card", "summary_large_image");
    upsertMetaTag("name", "twitter:title", seo.title);
    upsertMetaTag("name", "twitter:description", seo.description);
    upsertMetaTag(
      "name",
      "twitter:image",
      blogPost?.image?.path
        ? toAbsoluteUrl(blogPost.image.path)
        : `${SITE_URL}/assets/images/BANNER_LONG_1280.png`
    );

    removeSeoLinks();

    if (isBlogRoute || isUpdateRoute) {
      addManagedLink("canonical", canonicalUrl);
    }

    if (indexable) {
      if (!isBlogRoute && !isUpdateRoute) {
        addManagedLink("canonical", canonicalUrl);
      }

      const alternateLanguages = USE_CASE_ROUTE_PATHS.has(routePath)
        ? USE_CASE_LANGUAGES
        : isBlogRoute
        ? BLOG_CONTENT_LANGUAGES
        : isUpdateRoute
        ? UPDATE_CONTENT_LANGUAGES
        : SUPPORTED_LANGUAGE_CODES;
      alternateLanguages.forEach((supportedLanguage) => {
        const href = toAbsoluteUrl(toLocalizedPath(supportedLanguage, routePath));
        addManagedLink("alternate", href, { hreflang: supportedLanguage });
      });

      const defaultHref = toAbsoluteUrl(toLocalizedPath("en", routePath));
      addManagedLink("alternate", defaultHref, { hreflang: "x-default" });

      if (
        (isBlogRoute && (isBlogIndex || isStaticBlogPost)) ||
        isUpdateRoute ||
        (!isBlogRoute && (lang === "en" || lang === "ko"))
      ) {
        addManagedLink("alternate", `${canonicalUrl}.md`, {
          type: "text/markdown",
        });
      }
    }

    if (indexable && isBlogRoute) {
      upsertBlogStructuredData(
        canonicalUrl,
        contentLanguage,
        routePath,
        seo.title,
        seo.description,
        posts,
        blogPost
      );
    } else if (indexable && isUpdateRoute) {
      upsertUpdateStructuredData(
        canonicalUrl,
        updateContentLanguage,
        routePath,
        seo.title,
        seo.description,
        updateNote,
      );
    } else if (indexable) {
      upsertStructuredData(
        routePath,
        lang,
        canonicalUrl,
        seo.title,
        seo.description,
        translateSeoValue,
      );
    } else {
      removeStructuredData();
    }
  }, [i18n.resolvedLanguage, lang, location.pathname, posts, t]);

  return null;
};

export default SeoManager;
